/**
 * InventoryControlTowerPage — Inventory tab
 * ============================================
 *
 * Purpose: warehouse stock, availability, stockout risks and the
 * recommended action per at-risk SKU. The incoming retailer orders
 * table lives on the Orders & Automation tab now.
 *
 * The What-If Simulator opens in a right-side Sheet via the
 * "Simulate" button. The Smart Recommendation 4-strategy comparison
 * opens via the "Compare options" button next to each at-risk SKU.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { API_BASE } from '../../../lib/apiBase';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../../../components/ui/sheet';
import {
    Loader2, Warehouse, Package, Beaker, AlertCircle, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDistributionDemo } from './DistributionDemoContext';
import PageSummary from './components/PageSummary';
import WhatIfSimulator from './components/WhatIfSimulator';
import SmartRecommendation from './components/SmartRecommendation';

const API = `${API_BASE}/api`;

const formatEur = (n) => `€${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function InventoryControlTowerPage() {
    const { intel, sim } = useOutletContext() || {};
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [replenishmentData, setReplenishmentData] = useState(null);
    const [compareSku, setCompareSku] = useState(null);
    const [simOpen, setSimOpen] = useState(false);
    const { isSimulating, isPublished } = useDistributionDemo();

    useEffect(() => {
        axios.get(`${API}/admin/distribution-demo/inventory`)
            .then(res => setData(res.data))
            .catch(err => {
                console.error('Failed to load inventory data', err);
                toast.error('Failed to load inventory');
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        // Needed for the Compare options drawer (strategies per SKU).
        axios.get(`${API}/admin/distribution-demo/replenishment`)
            .then(res => setReplenishmentData(res.data))
            .catch(err => console.error('Failed to load replenishment data', err));
    }, []);

    const projections = sim?.projections || [];
    const atRisk = useMemo(() => projections.filter(p => p.supplyGapDays > 0), [projections]);
    const earliestStockout = useMemo(() => {
        const days = projections.filter(p => !p.digital && p.daysCover !== null).map(p => p.daysCover);
        return days.length ? Math.min(...days) : null;
    }, [projections]);
    const effectiveTotalUnits = useMemo(() => projections.reduce((s, p) => s + (p.digital ? 0 : (p.effectiveStock || 0)), 0), [projections]);

    const suggestionBySku = useMemo(() => {
        const map = {};
        (replenishmentData?.suggestions || []).forEach(s => { map[s.sku] = s; });
        return map;
    }, [replenishmentData]);

    if (loading) {
        return <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;
    }
    if (!data) return null;

    const { warehouses = [] } = data;

    const summaryMetrics = [
        {
            testid: 'kpi-available-units',
            label: 'Available units',
            value: effectiveTotalUnits.toLocaleString(),
            hint: isSimulating ? 'Reflects active scenario' : 'Across physical SKUs',
        },
        {
            testid: 'kpi-skus-at-risk',
            label: 'SKUs at risk',
            value: atRisk.length,
            accent: atRisk.length > 0 ? 'text-rose-600' : 'text-emerald-600',
            hint: atRisk.length > 0 ? atRisk.map(p => p.sku).join(', ') : 'All SKUs covered',
        },
        {
            testid: 'kpi-earliest-stockout',
            label: 'Earliest stockout',
            value: earliestStockout === null ? '—' : `${earliestStockout} days`,
            accent: earliestStockout !== null && earliestStockout < 56 ? 'text-rose-600' : 'text-slate-900',
            hint: earliestStockout === null ? 'No physical risk' : 'At current velocity',
        },
    ];

    const openCompare = (sku) => {
        if (!suggestionBySku[sku]) {
            toast.error('No replenishment options for this SKU');
            return;
        }
        setCompareSku(sku);
    };

    const compareSuggestion = compareSku && suggestionBySku[compareSku];
    const compareOptions = (replenishmentData?.options_by_sku || {})[compareSku] || [];
    const compareProjection = projections.find(p => p.sku === compareSku);

    return (
        <div className="space-y-4" data-testid="inventory-control-tower-page">
            <PageSummary metrics={summaryMetrics} />

            {/* Primary action row */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h3 className="text-sm font-semibold text-slate-900">Warehouse network</h3>
                    {!isPublished && (
                        <p className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Publish on the Releases tab first for the full connected story.
                        </p>
                    )}
                </div>
                <Sheet open={simOpen} onOpenChange={setSimOpen}>
                    <SheetTrigger asChild>
                        <Button variant={isSimulating ? 'default' : 'outline'} size="sm" data-testid="open-simulator-button">
                            <Beaker className="h-4 w-4 mr-1.5" />
                            {isSimulating ? 'Scenario active' : 'Simulate'}
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
                        <SheetHeader>
                            <SheetTitle>What-If Simulator</SheetTitle>
                        </SheetHeader>
                        <div className="mt-4">
                            <WhatIfSimulator projections={projections} />
                        </div>
                    </SheetContent>
                </Sheet>
            </div>

            {/* Warehouse list (compact rows, not big cards) */}
            <Card className="border-slate-200 overflow-hidden">
                <div className="divide-y divide-slate-200">
                    {warehouses.map(w => (
                        <div
                            key={w.warehouse_id}
                            className="flex items-center gap-3 p-3"
                            data-testid={`warehouse-${w.warehouse_id}`}
                        >
                            <div className={`h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0 ${w.primary ? 'bg-emerald-600' : 'bg-cyan-600'} text-white`}>
                                <Warehouse className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-semibold text-slate-900 truncate">{w.operator}</span>
                                    {w.primary && <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px]">Primary</Badge>}
                                </div>
                                <div className="text-xs text-slate-500">
                                    {w.location} · {w.connected_via} · lead {w.lead_time_days}d
                                </div>
                            </div>
                            <div className="text-right text-xs text-slate-500 flex-shrink-0 hidden sm:block">
                                {Object.entries(w.stock_by_sku || {}).filter(([, q]) => q !== null).map(([sku, q]) => (
                                    <div key={sku}><span className="font-mono">{sku.split('-').slice(-1)[0]}</span> · <strong>{q.toLocaleString()}</strong></div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Stock + risk per SKU (compact cards with inline recommended-action) */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                        <Package className="h-4 w-4 text-slate-500" />
                        Stock position
                    </h3>
                    <span className="text-xs text-slate-500">{atRisk.length > 0 ? `${atRisk.length} need action` : 'No action needed'}</span>
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                    {projections.map(p => {
                        const risk = p.supplyGapDays > 0;
                        return (
                            <Card
                                key={p.sku}
                                className={`p-3 border ${risk ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200'}`}
                                data-testid={`stock-card-${p.sku}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="text-[11px] text-slate-500">{p.sku}</div>
                                        <div className="text-xs font-semibold text-slate-900 truncate">{p.format}</div>
                                    </div>
                                    {risk && (
                                        <Badge variant="outline" className="border-rose-300 bg-rose-100 text-rose-700 text-[10px]">
                                            {p.supplyGapDays}d gap
                                        </Badge>
                                    )}
                                </div>
                                <div className="mt-2 flex items-baseline gap-1.5">
                                    <span className="text-xl font-bold text-slate-900 tabular-nums">
                                        {p.digital ? '∞' : p.effectiveStock.toLocaleString()}
                                    </span>
                                    {!p.digital && (
                                        <span className="text-[11px] text-slate-500">
                                            · {p.daysCover}d cover
                                        </span>
                                    )}
                                </div>
                                {risk && (
                                    <div className="mt-2 pt-2 border-t border-rose-200/70">
                                        <p className="text-[11px] text-slate-700">
                                            <strong>{formatEur(p.revenueAtRisk)}</strong> at risk · {p.unitsAtRisk.toLocaleString()} units
                                        </p>
                                        {suggestionBySku[p.sku] ? (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openCompare(p.sku)}
                                                className="mt-1.5 h-7 px-2 text-xs text-rose-700 hover:bg-rose-100"
                                                data-testid={`compare-options-${p.sku}`}
                                            >
                                                Compare options →
                                            </Button>
                                        ) : (
                                            <span className="text-[11px] text-slate-500">No replenishment options</span>
                                        )}
                                    </div>
                                )}
                                {!risk && !p.digital && (
                                    <div className="mt-2 text-[11px] text-emerald-700 flex items-center gap-1">
                                        <Check className="h-3 w-3" />
                                        Covered through next production cycle
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            </div>

            {/* Compare options drawer */}
            <Sheet open={!!compareSku} onOpenChange={(v) => { if (!v) setCompareSku(null); }}>
                <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle>Compare replenishment options</SheetTitle>
                    </SheetHeader>
                    {compareSuggestion && (
                        <div className="mt-4" data-testid={`compare-drawer-${compareSku}`}>
                            <SmartRecommendation
                                suggestion={compareSuggestion}
                                options={compareOptions}
                                projection={compareProjection}
                            />
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}
